/**
 * The task's prerequisites and dependents: what it waits for, and what
 * waits on it, each as a linked reference with its state.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { TaskView } from '../../api.js';
import { TaskStateChip, useWorkspaceCopy } from '../../components.js';
import { SectionHeading } from '../common.js';

export function TaskDependencySections({
  view,
}: {
  readonly view: TaskView;
}): ReactNode {
  const copy = useWorkspaceCopy();

  return (
    <>
      <section
        aria-label={copy.execution.dependencies}
        style={{ marginTop: 'var(--space-lg)' }}
      >
        <SectionHeading>{copy.execution.dependencies}</SectionHeading>
        {view.dependencies.length === 0 ? (
          <p style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)' }}>
            {copy.execution.noDependencies}
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {view.dependencies.map((edge) => (
              <li
                key={edge.taskId}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                <Link
                  to={`/tasks/${edge.taskId}`}
                  className="mj-ltr"
                  dir="ltr"
                  style={{
                    font: 'var(--type-body-tabular)',
                    color: 'var(--text-link)',
                  }}
                >
                  {edge.reference}
                </Link>
                <TaskStateChip status={edge.status} />
                <span
                  style={{
                    font: 'var(--type-caption)',
                    color: 'var(--text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 180,
                  }}
                >
                  {edge.title}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        aria-label={copy.execution.blocks}
        style={{ marginTop: 'var(--space-lg)' }}
      >
        <SectionHeading>{copy.execution.blocks}</SectionHeading>
        {view.dependents.length === 0 ? (
          <p style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)' }}>
            {copy.execution.blocksNothing}
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {view.dependents.map((edge) => (
              <li
                key={edge.taskId}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                <Link
                  to={`/tasks/${edge.taskId}`}
                  className="mj-ltr"
                  dir="ltr"
                  style={{
                    font: 'var(--type-body-tabular)',
                    color: 'var(--text-link)',
                  }}
                >
                  {edge.reference}
                </Link>
                <TaskStateChip status={edge.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
